import { z } from 'zod';
import type { StatutCandidature } from './candidature.js';
import type { DocumentResume } from './dossiers.js';
import type { CodeErreurCv } from './erreurs.js';
import type { FormatCv } from './format-cv.js';
import { emailsAvecPosition, normaliserTelephone } from './telephone.js';

/**
 * CV déposés à la main (docs/MODELE_DONNEES.md règle 17) : Tunisie Travail ne
 * transmet pas de CV, l'utilisateur le télécharge dans son espace recruteur puis
 * le dépose — sur la fiche, ou sur la page « CV à rattacher » d'où le worker le
 * rattache seul quand c'est sans ambiguïté. Le contenu du CV est une donnée :
 * il ne sert qu'à lire téléphone, e-mail et nom.
 */

export const FILE_CV_DEPOSES = 'cv-deposes';
export interface TravailAnalyseCv {
  depotId: string;
}
/** BullMQ refuse « : » dans un identifiant de travail. */
export const jobIdAnalyseCv = (depotId: string) => `cv-${depotId}`;
export const OPTIONS_TRAVAIL_ANALYSE_CV = {
  attempts: 2,
  backoff: { type: 'fixed', delay: 30_000 },
  removeOnComplete: { age: 3600, count: 500 },
  removeOnFail: { age: 86_400, count: 200 },
} as const;

/** Défaut de `retention.cv_a_rattacher.jours` : un CV non rattaché est effacé 30 jours après son dépôt. */
export const RETENTION_CV_A_RATTACHER_JOURS = 30;
export const FICHIERS_CV_PAR_SELECTION_MAX = 20;
/** En-tête du CV où le nom de la fiche doit figurer pour un rattachement automatique. */
export const LONGUEUR_EN_TETE_CV = 600;
/** Une analyse restée en cours plus longtemps est marquée échouée. */
export const ANALYSE_CV_ABANDON_HEURES = 6;
/** Téléphones et e-mails lus retenus au plus, par type. */
export const COORDONNEES_CV_MAX = 10;

export const STATUTS_DEPOT_CV = ['analyse', 'a_rattacher', 'rattache', 'ecarte', 'retire', 'expire'] as const;
export type StatutDepotCv = (typeof STATUTS_DEPOT_CV)[number];
export const LIBELLES_STATUT_DEPOT_CV: Record<StatutDepotCv, string> = {
  analyse: 'Lecture en cours',
  a_rattacher: 'À confirmer',
  rattache: 'Rattaché',
  ecarte: 'Écarté',
  retire: 'Retiré d’une fiche',
  expire: 'Effacé (délai dépassé)',
};

export const ORIGINES_DEPOT_CV = ['page', 'fiche'] as const;
export type OrigineDepotCv = (typeof ORIGINES_DEPOT_CV)[number];

export const MODES_RATTACHEMENT_CV = ['fiche', 'automatique', 'confirme', 'candidature_creee'] as const;
export type ModeRattachementCv = (typeof MODES_RATTACHEMENT_CV)[number];
export const LIBELLES_MODE_RATTACHEMENT_CV: Record<ModeRattachementCv, string> = {
  fiche: 'Déposé depuis la fiche',
  automatique: 'Rattaché automatiquement',
  confirme: 'Rattaché après confirmation',
  candidature_creee: 'Candidature créée depuis le CV',
};

export const MOTIFS_ATTENTE_CV = [
  'texte_illisible',
  'aucune_coordonnee',
  'aucune_personne',
  'plusieurs_personnes',
  'personne_exclue',
  'coordonnee_non_corroboree',
  'aucune_candidature_cible',
  'plusieurs_candidatures',
  'analyse_echouee',
] as const;
export type MotifAttenteCv = (typeof MOTIFS_ATTENTE_CV)[number];
export const LIBELLES_MOTIF_ATTENTE_CV: Record<MotifAttenteCv, string> = {
  texte_illisible: 'Texte du CV illisible : choisir la fiche à la main',
  aucune_coordonnee: 'Aucun téléphone ni e-mail lu dans le CV',
  aucune_personne: 'Le téléphone ou l’e-mail du CV ne correspond à aucune fiche',
  plusieurs_personnes: 'Les coordonnées du CV désignent plusieurs fiches',
  personne_exclue: 'Fiche « ne plus contacter » ou salarié : confirmation demandée',
  coordonnee_non_corroboree:
    'Coordonnée peut-être celle d’un tiers (référent, proche) : nom absent de l’en-tête du CV ou coordonnée non lue en premier',
  aucune_candidature_cible: 'Toutes les candidatures de cette personne ont déjà un CV',
  plusieurs_candidatures: 'Plusieurs candidatures sans CV : choisir laquelle',
  analyse_echouee: 'Lecture du CV impossible',
};

export const VUES_DEPOTS_CV = ['attente', 'rattaches', 'ecartes'] as const;
export type VueDepotsCv = (typeof VUES_DEPOTS_CV)[number];
export const listeDepotsCvSchema = z.object({
  vue: z.enum(VUES_DEPOTS_CV).default('attente'),
  /** Fiche candidat : les CV posés sur cette candidature ou sur ses origines du vivier, sans limite d'ancienneté. */
  candidature: z.string().uuid().optional(),
});
export type ListeDepotsCv = z.infer<typeof listeDepotsCvSchema>;
/** Statuts montrés par chaque onglet de la page. */
export const STATUTS_PAR_VUE_CV: Record<VueDepotsCv, readonly StatutDepotCv[]> = {
  attente: ['analyse', 'a_rattacher'],
  rattaches: ['rattache'],
  ecartes: ['ecarte', 'retire', 'expire'],
};
/** Lignes rendues au plus par onglet. */
export const DEPOTS_CV_PAR_VUE_MAX = 100;

// ---------------------------------------------------------------------------
// Coordonnées lues dans le CV
// ---------------------------------------------------------------------------

/** Domaines de l'entreprise et des plateformes : jamais l'adresse d'un candidat. */
export const DOMAINES_EXCLUS_CV: readonly string[] = [
  'tiba-invest.com',
  'tunisietravail.net',
  'tanitjobs.com',
  'keejob.com',
  'keejobmail.com',
];

/** Téléphones au format `+216…` et e-mails en minuscules à ignorer. */
export interface ExclusionsCv {
  telephones: readonly string[];
  emails: readonly string[];
}

/**
 * Exclusions d'une lecture : le paramètre `cv.telephones_exclus` (liste
 * `{ numero, libelle }` rédigée par le super admin ; une chaîne seule est
 * tolérée, une valeur invalide ignorée) et les e-mails des comptes de
 * l'application, exclus d'office.
 */
export function exclusionsCvDepuis(
  valeurParametre: unknown,
  emailsUtilisateurs: readonly (string | null | undefined)[],
): ExclusionsCv {
  const telephones = new Set<string>();
  if (Array.isArray(valeurParametre)) {
    for (const entree of valeurParametre) {
      const brut =
        typeof entree === 'string'
          ? entree
          : entree && typeof entree === 'object' && typeof (entree as { numero?: unknown }).numero === 'string'
            ? (entree as { numero: string }).numero
            : null;
      const numero = normaliserTelephone(brut);
      if (numero) telephones.add(numero);
    }
  }
  const emails = new Set<string>();
  for (const email of emailsUtilisateurs) {
    const propre = email?.trim().toLowerCase();
    if (propre) emails.add(propre);
  }
  return { telephones: [...telephones], emails: [...emails] };
}

/** Chaque type dans son ordre d'apparition dans le CV (l'ordre entre types : `coordonneesDansLOrdre`). */
export interface CoordonneesCv {
  telephones: string[];
  emails: string[];
}

const DATES = [
  /\b\d{1,2}[./-]\d{1,2}[./-](?:19|20)\d{2}\b/g, // JJ.MM.AAAA, JJ/MM/AAAA, JJ-MM-AAAA
  /\b(?:19|20)\d{2}[./-]\d{1,2}[./-]\d{1,2}\b/g, // AAAA-MM-JJ
  /\b\d{1,2}[./-](?:19|20)\d{2}\b/g, // MM/AAAA, MM.AAAA
  /\b(?:19|20)\d{2}\s*[-–—/]\s*(?:19|20)\d{2}\b/g, // AAAA - AAAA
];

/**
 * Masque les dates d'un CV : « 2019 - 2021 » ou « 12.05.2021 » ressemblent à un
 * numéro de huit chiffres une fois les séparateurs retirés. La longueur du texte
 * est conservée : les positions des coordonnées lues restent celles du CV.
 */
export function masquerDates(texte: string): string {
  return DATES.reduce((courant, motif) => courant.replace(motif, (date) => ' ;'.padEnd(date.length, ' ')), texte);
}

function occidentaliser(texte: string): string {
  return texte.replace(/[٠-٩۰-۹]/g, (c) => {
    const code = c.charCodeAt(0);
    return String(code - (code >= 0x06f0 ? 0x06f0 : 0x0660));
  });
}

/** Chiffres et séparateurs sur une même ligne (un saut de ligne coupe). */
const SEQUENCE_CHIFFRES = /(?:\+|00)?\d[\d \t.()/\u2010-\u2015-]{6,60}/g;
const INDICATIF_SEUL = /^(?:\+|00)?216$/;
const DEUX_ANNEES = /^(?:19|20)\d{2}(?:19|20)\d{2}$/;

/**
 * Numéros d'une séquence. Une séquence peut en porter plusieurs
 * (« 98 765 432 / 22 333 444 ») : on regroupe alors les blocs de chiffres
 * de gauche à droite jusqu'à former un numéro complet.
 */
function telephonesDeSequence(sequence: string): string[] {
  const direct = normaliserTelephone(sequence);
  if (direct) return [direct];
  const trouves: string[] = [];
  let tampon: string[] = [];
  const chiffres = (blocs: string[]) => blocs.join('').replace(/\D/g, '').length;
  const attendus = (blocs: string[]) =>
    INDICATIF_SEUL.test(blocs[0] ?? '') ? blocs[0]!.replace(/\D/g, '').length + 8 : 8;
  for (const bloc of sequence.match(/\+?\d+/g) ?? []) {
    tampon.push(bloc);
    while (tampon.length > 0 && chiffres(tampon) > attendus(tampon)) tampon.shift();
    if (tampon.length > 0 && chiffres(tampon) === attendus(tampon)) {
      const numero = normaliserTelephone(tampon.join(' '));
      if (numero) {
        trouves.push(numero);
        tampon = [];
      } else {
        tampon.shift();
      }
    }
  }
  return trouves;
}

function domaineExclu(email: string): boolean {
  const domaine = email.slice(email.lastIndexOf('@') + 1);
  return DOMAINES_EXCLUS_CV.some((d) => domaine === d || domaine.endsWith(`.${d}`));
}

/** Une coordonnée lue dans le CV, jamais stockée. */
export interface CoordonneeLueCv {
  champ: ChampCoordonneeCv;
  valeur: string;
  /** Position de sa première apparition dans le texte du CV. */
  position: number;
}

/**
 * Téléphones et e-mails d'un CV, sans doublon, dans l'ordre d'apparition
 * TOUS TYPES CONFONDUS, dates masquées, sans les nombres faits de deux années,
 * sans les domaines de l'entreprise et des plateformes ni les exclusions.
 */
export function coordonneesDansLOrdre(
  texte: string,
  exclusions: ExclusionsCv = { telephones: [], emails: [] },
): CoordonneeLueCv[] {
  const propre = masquerDates(occidentaliser(texte));
  const lues: CoordonneeLueCv[] = [];
  const vus = new Set<string>();
  for (const sequence of propre.matchAll(SEQUENCE_CHIFFRES)) {
    for (const numero of telephonesDeSequence(sequence[0])) {
      if (vus.has(numero) || DEUX_ANNEES.test(numero.slice(4))) continue;
      if (exclusions.telephones.includes(numero)) continue;
      vus.add(numero);
      lues.push({ champ: 'telephone', valeur: numero, position: sequence.index ?? 0 });
    }
  }
  for (const { email, position } of emailsAvecPosition(texte)) {
    if (domaineExclu(email) || exclusions.emails.includes(email)) continue;
    lues.push({ champ: 'email', valeur: email, position });
  }
  // Tri stable : deux numéros d'une même séquence gardent leur ordre.
  return lues.sort((a, b) => a.position - b.position);
}

/** Téléphones et e-mails d'un CV, chacun dans son ordre d'apparition ; dix au plus par type. */
export function coordonneesDuCv(
  texte: string,
  exclusions: ExclusionsCv = { telephones: [], emails: [] },
): CoordonneesCv {
  const lues = coordonneesDansLOrdre(texte, exclusions);
  const valeurs = (champ: ChampCoordonneeCv) =>
    lues.filter((c) => c.champ === champ).map((c) => c.valeur).slice(0, COORDONNEES_CV_MAX);
  return { telephones: valeurs('telephone'), emails: valeurs('email') };
}

/**
 * Nom du fichier chez l'utilisateur, gardé pendant l'attente pour rapprocher le
 * CV d'une fiche : sans chemin ni extension, sans caractère de contrôle ou de
 * mise en forme (bidirectionnalité), 120 caractères au plus.
 */
export function nomOrigineCv(brut: string | null | undefined): string | null {
  if (!brut) return null;
  const nom = (brut.split(/[\\/]/).pop() ?? '')
    .replace(/[\p{Cc}\p{Cf}]/gu, '')
    .replace(/\.[A-Za-z0-9]{1,5}$/, '')
    .replace(/\s+/g, ' ')
    .trim();
  const borne = Array.from(nom).slice(0, 120).join('').trim();
  return borne || null;
}

/** ` mot mot ` : sans accents, en minuscules, tout autre signe devient une espace. */
export function normaliserPourNom(texte: string): string {
  const simple = texte
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  return simple ? ` ${simple} ` : ' ';
}

/** Prénom et nom normalisés, ou null si l'un d'eux a moins de deux lettres. */
function prenomNom(prenom: string | null, nom: string | null): { p: string; n: string } | null {
  const p = normaliserPourNom(prenom ?? '').trim();
  const n = normaliserPourNom(nom ?? '').trim();
  if (p.replace(/ /g, '').length < 2 || n.replace(/ /g, '').length < 2) return null;
  return { p, n };
}

/** Prénom et nom contigus, dans un sens ou dans l'autre ; `zone` vient de normaliserPourNom. */
function nomContigu(zone: string, noms: { p: string; n: string }): boolean {
  return zone.includes(` ${noms.p} ${noms.n} `) || zone.includes(` ${noms.n} ${noms.p} `);
}

/** Intitulés admis autour du nom : « Nom et prénom : », « Mme », « CV ». */
const INTITULES_NOM = new Set([
  'nom', 'noms', 'prenom', 'prenoms', 'et', 'cv', 'curriculum', 'vitae',
  'm', 'mr', 'mme', 'mlle', 'monsieur', 'madame', 'mademoiselle', 'candidat', 'candidate',
]);
/** Dernier mot qui fait du nom suivant celui d'un parent : « Ahmed ben / Mohamed Trabelsi », « fils de ». */
const FILIATION = new Set(['ben', 'bent', 'bin', 'ibn', 'ould', 'fils', 'fille', 'de', 'epouse', 'ep']);

/**
 * Segments de mots d'une ligne, normalisés : la ponctuation de séparation
 * (virgule, deux-points, parenthèses, tiret isolé ou long, barre…) coupe ; une
 * adresse, un lien ou « Nom/Prénom » est retiré ; le point et le trait d'union
 * collé ne coupent pas (« Med. Ali », « Gharbi-Ben Salah »).
 */
function segmentsDeLigne(ligne: string): string[][] {
  return ligne
    .replace(/\S*[@/\\]\S*/g, '|')
    .replace(/\s[-\u2010\u2011]\s|[\u2012-\u2015]/g, '|')
    .replace(/[,;:|()[\]{}<>«»"“”•·\t]/g, '|')
    .split('|')
    .map((segment) => normaliserPourNom(segment).trim().split(' ').filter(Boolean))
    .filter((mots) => mots.length > 0);
}

/** Retire aux deux bouts d'un segment les intitulés et les nombres (« Mme », « 98 765 432 »). */
function sansIntitules(mots: readonly string[]): string {
  const accessoire = (mot: string) => INTITULES_NOM.has(mot) || /^\d+$/.test(mot);
  let debut = 0;
  let fin = mots.length;
  while (debut < fin && accessoire(mots[debut]!)) debut += 1;
  while (fin > debut && accessoire(mots[fin - 1]!)) fin -= 1;
  return mots.slice(debut, fin).join(' ');
}

/**
 * Le prénom ET le nom de la fiche dans les 600 premiers caractères du CV,
 * formant À EUX SEULS un segment de ligne (à l'intitulé, à la ponctuation et au
 * numéro près), ou chacun seul sur deux lignes qui se suivent. Jamais pris dans
 * un nom plus long — « Ahmed Ben Mohamed Trabelsi » n'est pas « Mohamed
 * Trabelsi », « Mohamed Ali Gharbi » n'est pas « Ali Gharbi » — ni juste après
 * un mot de filiation (« ben », « fils de ») : un proche au téléphone partagé
 * porte souvent une partie du nom du candidat.
 */
export function nomEnTete(texte: string, prenom: string | null, nom: string | null): boolean {
  const noms = prenomNom(prenom, nom);
  if (!noms) return false;
  const attendus = new Set([`${noms.p} ${noms.n}`, `${noms.n} ${noms.p}`]);
  const lignes = texte
    .slice(0, LONGUEUR_EN_TETE_CV)
    .split(/[\n\r\f\v\u2028\u2029]/)
    .map(segmentsDeLigne)
    .filter((ligne) => ligne.length > 0);
  const dernierMot = (segment: readonly string[] | undefined) => segment?.[segment.length - 1];
  for (let i = 0; i < lignes.length; i += 1) {
    const ligne = lignes[i]!;
    for (let k = 0; k < ligne.length; k += 1) {
      const precedent = k > 0 ? dernierMot(ligne[k - 1]) : dernierMot(lignes[i - 1]?.[lignes[i - 1]!.length - 1]);
      if (precedent !== undefined && FILIATION.has(precedent)) continue;
      const mots = ligne[k]!;
      const propre = sansIntitules(mots);
      if (attendus.has(mots.join(' ')) || attendus.has(propre)) return true;
      const suivante = lignes[i + 1];
      if (ligne.length === 1 && suivante?.length === 1) {
        const second = sansIntitules(suivante[0]!);
        if ((propre === noms.p && second === noms.n) || (propre === noms.n && second === noms.p)) return true;
      }
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Décision automatique (worker)
// ---------------------------------------------------------------------------

export interface PersonneTrouvee {
  personneId: string;
  nom: string | null;
  prenom: string | null;
  telephone: string | null;
  email: string | null;
  nePlusContacter: boolean;
  salarieEnPoste: boolean;
}

export interface CandidatureCible {
  candidatureId: string;
  statut: StatutCandidature;
  /** CV sur la candidature ou sur ses candidatures d'origine (`candidature_a_un_cv`). */
  aUnCv: boolean;
}

export type DecisionRattachementCv =
  | { type: 'automatique'; personneId: string; candidatureId: string }
  | { type: 'attente'; motif: Exclude<MotifAttenteCv, 'analyse_echouee'> };

/**
 * Rattachement automatique, volontairement strict (arbitrage du 2026-09-15) :
 * une seule personne non anonymisée désignée par un téléphone ou un e-mail du
 * CV, ni « ne plus contacter » ni salariée en poste, dont le prénom et le nom
 * sont en tête du CV (`nomEnTete`) ET dont une coordonnée est la toute première
 * lue, téléphones et e-mails confondus, exclusions appliquées — le téléphone
 * d'un référent ou d'un proche ne suffit jamais —, avec une seule candidature
 * sans CV hors embauche. Sinon le CV attend une confirmation.
 */
export function deciderRattachement(entree: {
  lisible: boolean;
  texte: string | null;
  coordonnees: CoordonneesCv;
  /** Personnes non anonymisées atteintes par un téléphone ou un e-mail lu. */
  personnes: readonly PersonneTrouvee[];
  /** Candidatures de la personne quand elle est seule ; [] sinon. */
  candidatures: readonly CandidatureCible[];
}): DecisionRattachementCv {
  const { coordonnees } = entree;
  if (!entree.lisible || !entree.texte?.trim()) return { type: 'attente', motif: 'texte_illisible' };
  if (coordonnees.telephones.length === 0 && coordonnees.emails.length === 0) {
    return { type: 'attente', motif: 'aucune_coordonnee' };
  }
  const atteintes = new Map<string, PersonneTrouvee>();
  for (const personne of entree.personnes) {
    const parTelephone = personne.telephone !== null && coordonnees.telephones.includes(personne.telephone);
    const parEmail = personne.email !== null && coordonnees.emails.includes(personne.email.toLowerCase());
    if (parTelephone || parEmail) atteintes.set(personne.personneId, personne);
  }
  if (atteintes.size === 0) return { type: 'attente', motif: 'aucune_personne' };
  if (atteintes.size > 1) return { type: 'attente', motif: 'plusieurs_personnes' };
  const personne = [...atteintes.values()][0]!;
  if (personne.nePlusContacter || personne.salarieEnPoste) return { type: 'attente', motif: 'personne_exclue' };

  // Première coordonnée du texte parmi celles retenues (exclusions déjà appliquées à `coordonnees`).
  const premiere = coordonneesDansLOrdre(entree.texte).find((c) =>
    (c.champ === 'telephone' ? coordonnees.telephones : coordonnees.emails).includes(c.valeur),
  );
  const premiereDeLaPersonne =
    premiere !== undefined &&
    (premiere.champ === 'telephone'
      ? premiere.valeur === personne.telephone
      : premiere.valeur === personne.email?.toLowerCase());
  if (!premiereDeLaPersonne || !nomEnTete(entree.texte, personne.prenom, personne.nom)) {
    return { type: 'attente', motif: 'coordonnee_non_corroboree' };
  }

  const cibles = entree.candidatures.filter((c) => !c.aUnCv && c.statut !== 'embauche');
  if (cibles.length === 0) return { type: 'attente', motif: 'aucune_candidature_cible' };
  if (cibles.length > 1) return { type: 'attente', motif: 'plusieurs_candidatures' };
  return { type: 'automatique', personneId: personne.personneId, candidatureId: cibles[0]!.candidatureId };
}

// ---------------------------------------------------------------------------
// Candidats probables (page « CV à rattacher »)
// ---------------------------------------------------------------------------

export const RAISONS_PROPOSITION_CV = [
  'telephone',
  'email',
  'telephone_proche',
  'email_proche',
  'nom_en_tete',
  'nom_texte',
  'nom_fichier',
] as const;
export type RaisonPropositionCv = (typeof RAISONS_PROPOSITION_CV)[number];

export const POIDS_RAISONS_CV: Record<RaisonPropositionCv, number> = {
  telephone: 100,
  email: 100,
  telephone_proche: 60,
  email_proche: 50,
  nom_en_tete: 45,
  nom_texte: 35,
  nom_fichier: 30,
};
/** Prime d'une personne dont aucune candidature n'a encore de CV. */
export const BONUS_SANS_CV = 10;
export const PROPOSITIONS_CV_MAX = 5;

export const LIBELLES_RAISON_PROPOSITION_CV: Record<RaisonPropositionCv, string> = {
  telephone: 'Même téléphone',
  email: 'Même e-mail',
  telephone_proche: 'Téléphone à un chiffre près',
  email_proche: 'E-mail proche',
  nom_en_tete: 'Nom en tête du CV',
  nom_texte: 'Nom dans le CV',
  nom_fichier: 'Nom dans le nom du fichier',
};

export interface PersonneComparable {
  id: string;
  nom: string | null;
  prenom: string | null;
  telephone: string | null;
  email: string | null;
  aUnCv: boolean;
}

export interface PropositionPersonne {
  personneId: string;
  raisons: RaisonPropositionCv[];
  score: number;
}

function telephoneProche(a: string, b: string): boolean {
  const x = a.slice(-8);
  const y = b.slice(-8);
  if (x.length !== 8 || y.length !== 8) return false;
  let ecarts = 0;
  for (let i = 0; i < 8; i += 1) if (x[i] !== y[i]) ecarts += 1;
  return ecarts === 1;
}

/** Vrai si une seule insertion, suppression ou substitution sépare les deux chaînes. */
export function distanceEditionUn(a: string, b: string): boolean {
  if (a === b || Math.abs(a.length - b.length) > 1) return false;
  const [court, long] = a.length <= b.length ? [a, b] : [b, a];
  let i = 0;
  while (i < court.length && court[i] === long[i]) i += 1;
  return court.length === long.length
    ? court.slice(i + 1) === long.slice(i + 1)
    : court.slice(i) === long.slice(i + 1);
}

function emailProche(lu: string, fiche: string): boolean {
  const [localLu, domaineLu] = [lu.slice(0, lu.lastIndexOf('@')), lu.slice(lu.lastIndexOf('@') + 1)];
  const [localFiche, domaineFiche] = [fiche.slice(0, fiche.lastIndexOf('@')), fiche.slice(fiche.lastIndexOf('@') + 1)];
  if (localLu === localFiche && localLu.length >= 4 && domaineLu !== domaineFiche) return true;
  return distanceEditionUn(lu, fiche);
}

/**
 * Personnes proches d'un CV, avec leurs raisons : même coordonnée, coordonnée à
 * une faute près, nom en tête du CV ou ailleurs dans le texte (exclusifs), nom
 * dans le nom du fichier. Score = somme des poids, plus une prime si la personne
 * n'a encore aucun CV ; tri par score puis par nom ; `max` personnes au plus.
 * `lu.coordonnees` est déjà filtré des exclusions.
 */
export function proposerPersonnes(
  lu: { texte: string | null; nomOrigine: string | null; coordonnees: CoordonneesCv },
  personnes: readonly PersonneComparable[],
  max = PROPOSITIONS_CV_MAX,
): PropositionPersonne[] {
  const texte = lu.texte ?? '';
  const zoneTete = normaliserPourNom(texte.slice(0, LONGUEUR_EN_TETE_CV));
  const zoneTexte = normaliserPourNom(texte);
  const zoneFichier = normaliserPourNom(lu.nomOrigine ?? '');
  const retenues: Array<PropositionPersonne & { personne: PersonneComparable }> = [];

  for (const personne of personnes) {
    const raisons: RaisonPropositionCv[] = [];
    const email = personne.email?.toLowerCase() ?? null;
    if (personne.telephone && lu.coordonnees.telephones.includes(personne.telephone)) raisons.push('telephone');
    else if (personne.telephone && lu.coordonnees.telephones.some((t) => telephoneProche(t, personne.telephone!))) {
      raisons.push('telephone_proche');
    }
    if (email && lu.coordonnees.emails.includes(email)) raisons.push('email');
    else if (email && lu.coordonnees.emails.some((e) => emailProche(e, email))) raisons.push('email_proche');

    const noms = prenomNom(personne.prenom, personne.nom);
    if (noms) {
      if (nomContigu(zoneTete, noms)) raisons.push('nom_en_tete');
      else if (nomContigu(zoneTexte, noms)) raisons.push('nom_texte');
      if (nomContigu(zoneFichier, noms)) raisons.push('nom_fichier');
    }
    if (raisons.length === 0) continue;
    const score = raisons.reduce((total, r) => total + POIDS_RAISONS_CV[r], personne.aUnCv ? 0 : BONUS_SANS_CV);
    retenues.push({ personneId: personne.id, raisons, score, personne });
  }

  const cle = (p: PersonneComparable) => `${p.nom ?? ''} ${p.prenom ?? ''}`;
  return retenues
    .sort(
      (a, b) =>
        b.score - a.score ||
        cle(a.personne).localeCompare(cle(b.personne), 'fr') ||
        a.personneId.localeCompare(b.personneId),
    )
    .slice(0, max)
    .map(({ personneId, raisons, score }) => ({ personneId, raisons, score }));
}

// ---------------------------------------------------------------------------
// Réponses de l'API
// ---------------------------------------------------------------------------

export interface CandidatProbable {
  personneId: string;
  nom: string | null;
  prenom: string | null;
  telephone: string | null;
  email: string | null;
  raisons: RaisonPropositionCv[];
  nePlusContacter: boolean;
  salarie: boolean;
  candidatures: Array<{
    candidatureId: string;
    annonceReference: string | null;
    annonceIntitule: string;
    source: string;
    statut: StatutCandidature;
    recueLe: string;
    aUnCv: boolean;
  }>;
}

/** Autre candidature qui porte déjà le même fichier. */
export interface CvDejaConnu {
  candidatureId: string;
  nom: string | null;
  prenom: string | null;
  annonceIntitule: string;
}

export interface CandidatureDuDepot {
  candidatureId: string;
  personneId: string;
  nom: string | null;
  prenom: string | null;
  telephone: string | null;
  email: string | null;
  anonymisee: boolean;
  annonceReference: string | null;
  annonceIntitule: string;
}

export interface DepotCvDetail {
  id: string;
  statut: StatutDepotCv;
  origine: OrigineDepotCv;
  motifAttente: MotifAttenteCv | null;
  mode: ModeRattachementCv | null;
  format: FormatCv;
  taille: number;
  /** En attente seulement. */
  nomOrigine: string | null;
  deposeLe: string;
  /** Nom de l'utilisateur. */
  deposePar: string;
  traiteLe: string | null;
  /** Null = traitement automatique. */
  traitePar: string | null;
  /** AAAA-MM-JJ : jour de la purge de 2 h 30 qui l'efface, en attente seulement. */
  expireLe: string | null;
  /** Null une fois le fichier effacé. */
  document: DocumentResume | null;
  /** Lues dans le texte, exclusions appliquées ; jamais stockées. */
  coordonnees: CoordonneesCv;
  candidature: CandidatureDuDepot | null;
  /** À confirmer (liste et détail) ; rattaché (détail, personne actuelle exclue) ; sinon []. */
  candidatsProbables: CandidatProbable[];
  aussiSur: CvDejaConnu[];
}

export const RESULTATS_DEPOT_CV = [
  'depose',
  'deja_en_attente',
  'deja_rattache',
  'rattache',
  'deja_present',
  'rattache_depot_existant',
] as const;
export type ResultatDepotCv = (typeof RESULTATS_DEPOT_CV)[number];

export interface ReponseDepotCv {
  resultat: ResultatDepotCv;
  depot: DepotCvDetail | null;
  documentId: string | null;
  aussiSur: CvDejaConnu[];
  /** `deja_rattache` seulement : candidatures sans CV des personnes proposées. */
  copiesPossibles: CandidatProbable[];
}

export type ChampCoordonneeCv = 'telephone' | 'email';

export interface BilanCompletionCv {
  ajoutes: ChampCoordonneeCv[];
  refus: Array<{ champ: ChampCoordonneeCv; code: CodeErreurCv | 'FICHE_ANONYMISEE'; message: string }>;
}

export interface ReponseActionDepotCv {
  depot: DepotCvDetail;
  completion: BilanCompletionCv | null;
}

export interface CompteursDepotsCv {
  analyse: number;
  aRattacher: number;
  retentionJours: number;
}

const TELEPHONE_NORMALISE = /^\+216[2-579]\d{7}$/;
const coordonneesDemandees = {
  telephone: z.string().regex(TELEPHONE_NORMALISE, 'Téléphone au format +216XXXXXXXX').optional(),
  email: z.string().trim().toLowerCase().email('E-mail invalide').max(254).optional(),
};

/** POST /cv-a-rattacher/:id/rattacher — avec, sur demande explicite, la coordonnée lue à ajouter à la fiche. */
export const rattachementCvSchema = z.object({ candidatureId: z.string().uuid(), ...coordonneesDemandees }).strict();
export type RattachementCv = z.infer<typeof rattachementCvSchema>;

export const deplacementCvSchema = z.object({ candidatureId: z.string().uuid() }).strict();
export type DeplacementCv = z.infer<typeof deplacementCvSchema>;

export const completionCoordonneesCvSchema = z
  .object(coordonneesDemandees)
  .strict()
  .refine((v) => Boolean(v.telephone || v.email), { message: 'Choisir le téléphone ou l’e-mail à ajouter' });
export type CompletionCoordonneesCv = z.infer<typeof completionCoordonneesCvSchema>;

/**
 * POST /cv-a-rattacher/:id/creer-candidature — « Affecter à une annonce ».
 * Le CV est celui d'une personne qu'aucune fiche ne connaît : le responsable RH
 * donne l'identité et choisit l'annonce, l'API crée la fiche et la candidature
 * puis y rattache le CV. Au moins une coordonnée est exigée : c'est la clé qui
 * évite les doublons de personnes (docs/INGESTION_EMAIL.md § Personne et doublons)
 * et le seul moyen de rappeler le candidat.
 */
export const creationCandidatureCvSchema = z
  .object({
    annonceId: z.string().uuid(),
    prenom: z.string().trim().min(2, 'Prénom : 2 caractères au moins').max(80),
    nom: z.string().trim().min(2, 'Nom : 2 caractères au moins').max(80),
    ...coordonneesDemandees,
  })
  .strict()
  .refine((v) => Boolean(v.telephone || v.email), {
    message: 'Donner au moins le téléphone ou l’e-mail du candidat',
  });
export type CreationCandidatureCv = z.infer<typeof creationCandidatureCvSchema>;

/** Source portée par une candidature née d'un CV déposé à la main. */
export const SOURCE_CANDIDATURE_CV = 'direct';

export interface ReponseCandidatureCv {
  depot: DepotCvDetail;
  candidatureId: string;
  /** Faux : la personne était déjà candidate sur cette annonce, le CV a rejoint sa candidature. */
  candidatureCreee: boolean;
  /** Faux : fiche retrouvée par le téléphone ou l'e-mail, complétée sans rien écraser. */
  personneCreee: boolean;
}

/**
 * POST /cv-a-rattacher/:id/supprimer-candidature — « Supprimer la candidature ».
 * Défait une affectation : la candidature part avec son CV, ses appels, son
 * évaluation, sa charte et ses justificatifs. La fiche de la personne ne part
 * que si ce dossier était le dernier — sinon elle garde ses autres
 * candidatures. Une candidature embauchée ne se supprime pas.
 */
export interface BilanSuppressionCandidature {
  candidatureId: string;
  /** Vrai : la personne n'avait plus d'autre candidature, sa fiche est partie aussi. */
  personneSupprimee: boolean;
  /** Fichiers inscrits pour effacement (CV, lettres, chartes, justificatifs, message d'origine). */
  fichiers: number;
}

export const rechercheFicheCvSchema = z.object({
  q: z.string().trim().min(3, '3 caractères au moins').max(60),
});
export type RechercheFicheCv = z.infer<typeof rechercheFicheCvSchema>;
